/**
 * Toast-System — leichtgewichtiger Singleton-Store mit Subscribe-Listener.
 *
 * Verwendung:
 *   showToast('Gespeichert')
 *   showToast('Fehler beim Laden', { kind: 'error' })
 *   showToast('VK übernommen', { kind: 'success', duration: 2000 })
 *
 * Im UI:
 *   const toasts = useToasts()  // subscribed list
 *   <Toaster /> oben in App.tsx ein einziges Mal mounten
 */

import { useEffect, useState } from 'react'

export type ToastKind = 'success' | 'info' | 'error'

export interface Toast {
  id: number
  message: string
  kind: ToastKind
  /** Wanduhrzeit (ms), nach der der Toast automatisch verschwindet. */
  expiresAt: number
}

interface ShowOptions {
  kind?: ToastKind
  /** Dauer in ms. Default 3500 für success/info, 6000 für error. */
  duration?: number
}

const toasts: Toast[] = []
const listeners = new Set<() => void>()
let nextId = 1

function notify(): void {
  for (const l of listeners) l()
}

function scheduleCleanup(): void {
  if (typeof window === 'undefined') return
  // Räume abgelaufene Toasts beim nächsten Frame raus.
  const earliest = toasts.reduce(
    (acc, t) => Math.min(acc, t.expiresAt),
    Number.POSITIVE_INFINITY,
  )
  if (!Number.isFinite(earliest)) return
  const wait = Math.max(0, earliest - Date.now())
  window.setTimeout(() => {
    const now = Date.now()
    const before = toasts.length
    for (let i = toasts.length - 1; i >= 0; i--) {
      if (toasts[i].expiresAt <= now) toasts.splice(i, 1)
    }
    if (toasts.length !== before) notify()
    if (toasts.length > 0) scheduleCleanup()
  }, wait + 16)
}

export function showToast(message: string, opts: ShowOptions = {}): number {
  const kind: ToastKind = opts.kind ?? 'success'
  const defaultDuration = kind === 'error' ? 6000 : 3500
  const duration = opts.duration ?? defaultDuration
  const toast: Toast = {
    id: nextId++,
    message,
    kind,
    expiresAt: Date.now() + duration,
  }
  toasts.push(toast)
  notify()
  scheduleCleanup()
  return toast.id
}

export function dismissToast(id: number): void {
  const i = toasts.findIndex((t) => t.id === id)
  if (i >= 0) {
    toasts.splice(i, 1)
    notify()
  }
}

export function useToasts(): Toast[] {
  const [, force] = useState(0)
  useEffect(() => {
    const onChange = () => force((n) => n + 1)
    listeners.add(onChange)
    return () => {
      listeners.delete(onChange)
    }
  }, [])
  return toasts.slice()
}
